import {
  Button,
  Card,
  CardHeader,
  Disclosure,
  DisclosureContent,
  DisclosureTrigger,
  Field,
  Flex,
  Input,
  Spinner,
  Switch,
  Text,
  Textarea,
} from '@optiaxiom/react';

import { styles } from './AgentBasicFields.css';

export type AgentBasicFieldsProps = {
  name: string;
  isActive: boolean;
  agentId: string;
  idFieldStatus?: { color: 'fg.secondary' | 'fg.success' | 'fg.error'; message: string } | null;
  isCheckingAvailability?: boolean;
  availabilityStatus?: { suggestions?: string[] };
  description?: string;
  onChange: (field: string, value: unknown) => void;
  onSuggestionClick?: (suggestion: string) => void;
  showErrors?: boolean;
};

export function AgentBasicFields({
  name,
  isActive,
  agentId,
  idFieldStatus,
  isCheckingAvailability,
  availabilityStatus = {},
  description = '',
  onChange,
  onSuggestionClick,
  showErrors = false,
}: AgentBasicFieldsProps) {
  // Compute error messages for required fields
  const nameError = showErrors && !name?.trim() ? 'Name is required' : undefined;
  const agentIdError = showErrors && !agentId?.trim() ? 'Id is required' : undefined;
  const descriptionError =
    showErrors && !description?.trim() ? 'Description is required' : undefined;
  return (
    <Flex className={styles.container}>
      <Card bg="bg.secondary" px="8" py="4">
        <Disclosure w="full" defaultOpen>
          <CardHeader>
            <DisclosureTrigger>
              <Text className={styles.fieldTitle}>Details</Text>
            </DisclosureTrigger>
          </CardHeader>
          <DisclosureContent>
            <Flex pt="24" pb="20" px="24">
              <Field label="Name" required error={nameError}>
                <Flex flexDirection="row" justifyContent="start" alignItems="center">
                  <Input
                    value={name || ''}
                    placeholder="Agent Name"
                    className={styles.nameInput}
                    onChange={e => onChange('name', e.target.value)}
                  />
                  <Switch
                    checked={isActive}
                    size="md"
                    onCheckedChange={checked => onChange('is_active', checked)}
                  >
                    Active
                  </Switch>
                </Flex>
              </Field>
              <Field label="Id" required error={agentIdError}>
                <Flex flexDirection="column" gap="8">
                  <Flex flexDirection="row" justifyContent="start" alignItems="center">
                    <Input
                      value={agentId}
                      placeholder="agent_id"
                      className={styles.idInput}
                      onChange={e => onChange('agent_id', e.target.value.replace(/\s+/g, ''))}
                      onBlur={e => onChange('agent_id', e.target.value.replace(/@/g, ''))}
                    />
                    <Text fontSize="md" color="fg.secondary">
                      @{agentId?.toLowerCase().replace(/\s+/g, '_')}
                    </Text>
                  </Flex>
                  {idFieldStatus && (
                    <Flex flexDirection="row" alignItems="center" gap="8">
                      {isCheckingAvailability && <Spinner size="sm" />}
                      <Text fontSize="sm" color={idFieldStatus.color}>
                        {idFieldStatus.message}
                      </Text>
                    </Flex>
                  )}
                  {availabilityStatus.suggestions && availabilityStatus.suggestions.length > 0 && (
                    <Flex flexDirection="column" gap="4">
                      <Text fontSize="sm" color="fg.secondary">
                        Suggestions:
                      </Text>
                      <Flex flexDirection="row" gap="8" flexWrap="wrap">
                        {availabilityStatus.suggestions.map(suggestion => (
                          <Button
                            key={suggestion}
                            appearance="subtle"
                            size="sm"
                            onClick={() => onSuggestionClick && onSuggestionClick(suggestion)}
                          >
                            {suggestion}
                          </Button>
                        ))}
                      </Flex>
                    </Flex>
                  )}
                </Flex>
              </Field>
              <Field
                label="Description"
                info="Describe this agent's purpose and functionality"
                required
                error={descriptionError}
              >
                <Textarea
                  value={description || ''}
                  className={styles.descriptionInput}
                  placeholder="Describe your agent"
                  onChange={e => onChange('description', e.target.value)}
                  resize="vertical"
                />
              </Field>
            </Flex>
          </DisclosureContent>
        </Disclosure>
      </Card>
    </Flex>
  );
}
